import { useTranslation } from '../hooks/useTranslation';
import { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Fingerprint } from 'lucide-react';

interface PasskeyRegistrationDialogProps {
  open: boolean;
  onClose: () => void;
  onRegister: () => Promise<void>;
}

const PasskeyRegistrationDialog = ({
  open,
  onClose,
  onRegister,
}: PasskeyRegistrationDialogProps) => {
  const { translate } = useTranslation();
  const [isIOS, setIsIOS] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);

  useEffect(() => {
    // Detect iOS Safari
    const detectIOS = () => {
      const userAgent = navigator.userAgent;
      const isIOSDevice =
        /iPad|iPhone|iPod/.test(userAgent) ||
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      const isSafari = /^((?!chrome|android).)*safari/i.test(userAgent);
      return isIOSDevice && isSafari;
    };

    setIsIOS(detectIOS());
  }, []);

  const handleRegister = async () => {
    try {
      setIsRegistering(true);
      await onRegister();
      onClose();
    } catch (error) {
      console.error('Passkey registration error:', error);
      // Error handling is done by the auth provider
    } finally {
      setIsRegistering(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Fingerprint className="h-5 w-5" />
            {translate('pos.auth.register_passkey')}
          </DialogTitle>
          <DialogDescription>
            {translate('pos.auth.register_passkey_description')}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          {isIOS && (
            <Alert>
              <AlertDescription>
                {translate('pos.auth.register_passkey_ios_guidance')}
              </AlertDescription>
            </Alert>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isRegistering}>
            {translate('pos.common.action.skip')}
          </Button>
          <Button onClick={handleRegister} disabled={isRegistering}>
            {isRegistering
              ? 'Registering...'
              : translate('pos.auth.register_passkey_button')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PasskeyRegistrationDialog;
